'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, LogIn, Loader2, AlertCircle, GraduationCap } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { recordLoginLog } from '@/lib/services/loginLogService';
import Toast from '@/components/ui/Toast';

interface StudentLoginFormData {
  nis: string;
  tanggal_lahir: string;
}

export default function StudentLoginForm() {
  const router = useRouter();
  const [nis, setNis] = useState('');
  const [tanggalLahir, setTanggalLahir] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isShaking, setIsShaking] = useState(false);
  const [nisError, setNisError] = useState<string | null>(null);
  const [tglError, setTglError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNisError(null);
    setTglError(null);
    setLoginError(null);

    let valid = true;
    if (!nis.trim()) { setNisError('NIS wajib diisi.'); valid = false; }
    else if (nis.trim().length < 3) { setNisError('NIS terlalu pendek.'); valid = false; }
    if (!tanggalLahir.trim()) { setTglError('Password (tanggal lahir) wajib diisi.'); valid = false; }
    else if (!/^\d{8}$/.test(tanggalLahir.trim())) { setTglError('Format tanggal lahir harus 8 digit angka (DDMMYYYY).'); valid = false; }
    if (!valid) return;

    setIsLoading(true);
    try {
      const supabase = createClient();
      const { data: siswa, error } = await supabase
        .from('siswa_master')
        .select('id, nis, nama, kelas, jurusan, tanggal_lahir, tgl_lahir, email, no_hp, alamat, jenis_kelamin, tahun_pelajaran, kompetensi_keahlian_id, kompetensi_keahlian(nama)')
        .eq('nis', nis.trim())
        .eq('is_active', true)
        .maybeSingle();

      if (error) throw error;
      if (!siswa) {
        throw new Error('NIS tidak ditemukan atau akun tidak aktif.');
      }

      // Verify tanggal_lahir as DDMMYYYY
      let dobMatch = false;
      if (siswa.tgl_lahir) {
        // tgl_lahir is date: YYYY-MM-DD
        const parts = siswa.tgl_lahir.split('-');
        if (parts.length === 3) {
          const formatted = `${parts[2]}${parts[1]}${parts[0]}`;
          dobMatch = formatted === tanggalLahir.trim();
        }
      }
      if (!dobMatch && siswa.tanggal_lahir) {
        // tanggal_lahir is text, try YYYY-MM-DD or DD/MM/YYYY
        const tl = siswa.tanggal_lahir.trim();
        if (tl.includes('-')) {
          const parts = tl.split('-');
          if (parts.length === 3) {
            const formatted = `${parts[2].padStart(2,'0')}${parts[1].padStart(2,'0')}${parts[0]}`;
            dobMatch = formatted === tanggalLahir.trim();
          }
        } else if (tl.includes('/')) {
          const parts = tl.split('/');
          if (parts.length === 3) {
            const formatted = `${parts[0].padStart(2,'0')}${parts[1].padStart(2,'0')}${parts[2]}`;
            dobMatch = formatted === tanggalLahir.trim();
          }
        }
      }

      if (!dobMatch) {
        await recordLoginLog({ role: 'siswa', username: nis.trim(), action: 'login_error', error_msg: 'Password (tanggal lahir) tidak sesuai.' });
        throw new Error('Password (tanggal lahir) tidak sesuai.');
      }

      // Store session in localStorage
      if (typeof window !== 'undefined') {
        // Fetch kelas from el_siswa (may have multiple rows per tahun pembelajaran)
        // Prefer the row linked to active el_tahun_pembelajaran, fallback to latest
        let kelasNama: string | null = null;
        let tahunPembelajaranLabel: string | null = siswa.tahun_pelajaran ?? null;
        try {
          const { data: elSiswaRows } = await supabase
            .from('el_siswa')
            .select('kelas, kelas_id, tahun_pembelajaran, tahun_pembelajaran_id, el_kelas(nama_kelas), el_tahun_pembelajaran(label, is_active)')
            .eq('ref_id', siswa.id)
            .order('tahun_pembelajaran_id', { ascending: false });

          if (elSiswaRows && elSiswaRows.length > 0) {
            // Prefer row with active tahun pembelajaran
            type ElSiswaRow = {
              kelas: string | null;
              kelas_id: string | null;
              tahun_pembelajaran: string | null;
              tahun_pembelajaran_id: string | null;
              el_kelas: { nama_kelas?: string } | null;
              el_tahun_pembelajaran: { label?: string; is_active?: boolean } | null;
            };
            const rows = elSiswaRows as ElSiswaRow[];
            const activeRow = rows.find(r => r.el_tahun_pembelajaran?.is_active === true) ?? rows[0];
            kelasNama = activeRow.el_kelas?.nama_kelas ?? activeRow.kelas ?? null;
            tahunPembelajaranLabel = activeRow.el_tahun_pembelajaran?.label ?? activeRow.tahun_pembelajaran ?? tahunPembelajaranLabel;
          }
        } catch {
          // fallback to siswa_master.kelas if el_siswa query fails
          kelasNama = siswa.kelas ?? null;
        }

        // Fallback: if kelas still not found, query siswa_kelas_riwayat directly
        // This is the authoritative source when bulk-setting-kelas assigns a class
        if (!kelasNama) {
          try {
            const { data: riwayatRows } = await supabase
              .from('siswa_kelas_riwayat')
              .select('nama_kelas_snapshot, status')
              .eq('siswa_id', siswa.id)
              .in('status', ['AKTIF', 'LULUS'])
              .order('created_at', { ascending: false })
              .limit(1);

            if (riwayatRows && riwayatRows.length > 0) {
              kelasNama = riwayatRows[0].nama_kelas_snapshot ?? null;
            }
          } catch {
            // ignore, kelasNama stays null
          }
        }

        localStorage.setItem('siswa_session', JSON.stringify({
          id: siswa.id,
          nis: siswa.nis,
          nama: siswa.nama,
          kelas: kelasNama ?? siswa.kelas,
          jurusan: siswa.jurusan,
          kompetensiNama: (siswa.kompetensi_keahlian as { nama?: string } | null)?.nama ?? siswa.jurusan ?? null,
          email: siswa.email,
          no_hp: siswa.no_hp,
          alamat: siswa.alamat,
          jenis_kelamin: siswa.jenis_kelamin,
          tahun_pelajaran: tahunPembelajaranLabel ?? siswa.tahun_pelajaran,
          loginAt: new Date().toISOString(),
        }));
      }

      await recordLoginLog({ role: 'siswa', username: nis.trim(), action: 'login_sukses' });
      setToast({ message: 'Login berhasil! Mengalihkan ke dashboard...', type: 'success' });
      setTimeout(() => router.push('/siswa/dashboard'), 800);
    } catch (err: any) {
      setLoginError(err?.message || 'Terjadi kesalahan. Coba lagi.');
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
            <GraduationCap size={20} className="text-blue-700" />
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground tracking-wide uppercase">
              SMK Negeri 5 Malang
            </p>
            <h1 className="text-xl font-bold text-foreground">Login Siswa</h1>
          </div>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Masuk menggunakan <strong className="text-foreground">NIS</strong> dan{' '}
          <strong className="text-foreground">tanggal lahir</strong> kamu sebagai password.
        </p>
      </div>

      {/* Error alert */}
      {loginError && (
        <div
          className={`flex items-start gap-3 p-3.5 rounded-xl bg-red-50 border border-red-200 mb-5 ${
            isShaking ? 'animate-shake' : ''
          }`}
        >
          <AlertCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700 leading-snug">{loginError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        {/* NIS Field */}
        <div>
          <label htmlFor="nis" className="block text-sm font-semibold text-foreground mb-1.5">
            NIS (Nomor Induk Siswa)<span className="text-red-500 ml-1">*</span>
          </label>
          <p className="text-xs text-muted-foreground mb-2">Masukkan NIS kamu sesuai format yang tertera di kartu pelajar.</p>
          <input
            id="nis"
            type="text"
            autoComplete="username"
            placeholder="Contoh: 14476/1073.127"
            value={nis}
            onChange={(e) => setNis(e.target.value)}
            className={`w-full px-4 py-3 rounded-xl border bg-white text-sm font-medium text-foreground placeholder:text-muted-foreground/60 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-300 ${
              nisError ? 'border-red-400 bg-red-50/50' : 'border-border hover:border-blue-300'
            }`}
          />
          {nisError && (
            <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
              <AlertCircle size={11} />{nisError}
            </p>
          )}
        </div>

        {/* Tanggal Lahir / Password Field */}
        <div>
          <label htmlFor="tanggal_lahir" className="block text-sm font-semibold text-foreground mb-1.5">
            Password (Tanggal Lahir)<span className="text-red-500 ml-1">*</span>
          </label>
          <p className="text-xs text-muted-foreground mb-2">
            Format: <span className="font-mono font-semibold text-blue-700">DDMMYYYY</span> — contoh: lahir 15 Agustus 2007 →{' '}
            <span className="font-mono font-semibold text-blue-700">15082007</span>
          </p>
          <div className="relative">
            <input
              id="tanggal_lahir"
              type={showPassword ? 'text' : 'password'}
              inputMode="numeric"
              autoComplete="current-password"
              placeholder="DDMMYYYY"
              maxLength={8}
              value={tanggalLahir}
              onChange={(e) => setTanggalLahir(e.target.value)}
              className={`w-full px-4 py-3 pr-12 rounded-xl border bg-white text-sm font-medium text-foreground placeholder:text-muted-foreground/60 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-300 font-mono ${
                tglError ? 'border-red-400 bg-red-50/50' : 'border-border hover:border-blue-300'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-all duration-150"
              aria-label={showPassword ? 'Sembunyikan password' : 'Tampilkan password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {tglError && (
            <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
              <AlertCircle size={11} />{tglError}
            </p>
          )}
        </div>

        {/* Format hint card */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
          <p className="text-xs font-semibold text-blue-800 mb-1.5">📅 Panduan Format Password</p>
          <div className="grid grid-cols-3 gap-2 text-center">
            {[
              { label: 'DD', desc: 'Tanggal', example: '03' },
              { label: 'MM', desc: 'Bulan', example: '07' },
              { label: 'YYYY', desc: 'Tahun', example: '2008' },
            ].map((item) => (
              <div key={`hint-${item.label}`} className="bg-white rounded-lg p-2 border border-blue-100">
                <p className="font-mono font-bold text-blue-700 text-sm">{item.label}</p>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
                <p className="font-mono text-xs text-blue-500">{item.example}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-blue-700 mt-2 text-center font-medium">
            Contoh: 03072008 → tanggal 3 Juli 2008
          </p>
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 btn-student disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ minHeight: '52px' }}
        >
          {isLoading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              <span>Memverifikasi...</span>
            </>
          ) : (
            <>
              <LogIn size={18} />
              <span>Masuk ke Dashboard</span>
            </>
          )}
        </button>

        {/* Daftar Ulang Link */}
        <div className="text-center pt-2">
          <p className="text-xs text-muted-foreground mb-2">Siswa baru tahun 2026?</p>
          <a
            href="/daftar-ulang"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-blue-200 bg-blue-50 text-blue-700 text-sm font-semibold hover:bg-blue-100 hover:border-blue-300 transition-all duration-150"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            Daftar Ulang Siswa Baru
          </a>
        </div>
      </form>
    </div>
  );
}